import React, { createContext, useState } from 'react';
import PropTypes from 'prop-types';
import { useGetMatchRosterRoundQuery } from 'common/store/modules/api/cfs-api';
import { useGetMatchQuery } from 'common/store/modules/api/afl-api';
import { getTranslation } from 'common/react/utils/translations';
import { useSingleSiteSetting } from 'common/react/hooks/use-single-site-setting';
import { LastUpdated } from 'common/react/components/LastUpdated';
import { MCTeamLineupsSkeletonLoader } from './MCTeamLineupsSkeleton';
import { FieldListView } from 'widgets/team-lineups-v2/js/components/item/components/FieldListView';
import { InsAndOuts } from 'widgets/team-lineups-v2/js/components/item/components/ins-and-outs/InsAndOuts';
import { Milestones } from 'widgets/team-lineups-v2/js/components/item/components/Milestones';
import { LateChanges } from 'widgets/team-lineups-v2/js/components/item/components/LateChanges';
import { EmptyState } from 'common/react/components/EmptyState';
import { TeamLineupsLegend } from 'widgets/team-lineups-v2/js/components/TeamLineupsLegend';

/**
 * Fetches the match / roster data and initialises the Match Centre Team Lineups.
 *
 * @param {string} matchId The Pulse match ID.
 * @param {string} providerCompetitionId The provider competition ID.
 * @param {string} providerRoundId The provider round ID.
 * @param {string} providerMatchId The provider match ID.
 *
 * @returns {JSX.Element} The empty state or the Match Centre Team Lineups.
 */

export const TeamLineupsContext = createContext('');

export const MCTeamLineups = ({
    matchId,
    providerCompetitionId,
    providerRoundId,
    providerMatchId,
    compSeasonId
}) => {
    const [selectedTeam, setSelectedTeam] = useState('-1');
    const [selectedTabView, setSelectedTabView] = useState('field');

    // Capture team and view (field/list) selection in state
    const handleTeamChange = (team) => {
        setSelectedTeam(team);
    };

    const handleTabViewChange = (view) => {
        setSelectedTabView(view);
    };

    const {
        data: rosterData,
        isFetching: isFetchingRosterData,
        isError: rosterDataError
    } = useGetMatchRosterRoundQuery({ roundId: providerRoundId });

    const {
        data: singleMatchData,
        isFetching: isFetchingSingleMatchData,
        isError: singleMatchDataError
    } = useGetMatchQuery({ id: matchId });

    const matchRosterData = rosterData?.find(
        (roster) => roster.match.matchId === providerMatchId
    );

    const shouldShowSubs = PULSE.app.common.match.shouldShowSubs(
        matchRosterData?.matchRoster?.status
    );

    // Determine what team value has been selected
    const objHasValue = (obj, value) => Object.values(obj).includes(value);
    let teamDisplay = '';
    if (singleMatchData) {
        if (selectedTeam !== '-1') {
            if (objHasValue(singleMatchData?.[0]?.home?.team, selectedTeam)) {
                teamDisplay = 'home';
            } else {
                teamDisplay = 'away';
            }
            ``;
        } else {
            teamDisplay = 'both';
        }
    }

    const isAFLW =
        PULSE.app.common.match
            .getCompAbbr(providerCompetitionId)
            .toLowerCase() === 'aflw';

    const selectedRegion = useSingleSiteSetting('region');
    const lastUpdated = moment(matchRosterData?.matchRoster?.lastUpdated);

    return (
        <>
            {isFetchingRosterData || isFetchingSingleMatchData ? (
                <MCTeamLineupsSkeletonLoader />
            ) : rosterDataError ||
              singleMatchDataError ||
              !rosterData ||
              !singleMatchData ? (
                <EmptyState
                    title={getTranslation('label.teamLineups.noDataTitle')}
                    summary={getTranslation('label.teamLineups.noDataText')}
                    cssClass="competition-nav__empty-state"
                />
            ) : matchRosterData && singleMatchData ? (
                <>
                    <TeamLineupsContext.Provider value={'match-centre'}>
                        <div className="mc-team-lineups__inner-wrapper">
                            <FieldListView
                                match={matchRosterData}
                                isAFLW={isAFLW}
                                compSeasonId={compSeasonId}
                                onTeamChange={handleTeamChange}
                                onTabViewChange={handleTabViewChange}
                            />
                            <InsAndOuts
                                match={matchRosterData}
                                shouldShowSubs={shouldShowSubs}
                                teamDisplay={teamDisplay}
                                isAFLW={isAFLW}
                                selectedTabView={selectedTabView}
                            />
                            <div className="mc-team-lineups__late-changes-wrapper">
                                <Milestones
                                    match={matchRosterData}
                                    teamDisplay={teamDisplay}
                                    selectedTabView={selectedTabView}
                                />
                                {shouldShowSubs && (
                                    <LateChanges
                                        match={matchRosterData}
                                        teamDisplay={teamDisplay}
                                        selectedTabView={selectedTabView}
                                    />
                                )}
                            </div>
                            <LastUpdated
                                cssClass="team-lineups__last-updated"
                                lastUpdated={lastUpdated}
                                selectedRegion={selectedRegion}
                            />
                        </div>
                        <TeamLineupsLegend />
                    </TeamLineupsContext.Provider>
                </>
            ) : (
                <EmptyState
                    title={getTranslation('label.teamLineups.noDataTitle')}
                    summary={getTranslation('label.teamLineups.noDataText')}
                    cssClass="competition-nav__empty-state"
                />
            )}
        </>
    );
};

MCTeamLineups.propTypes = {
    matchId: PropTypes.string.isRequired,
    providerCompetitionId: PropTypes.string.isRequired,
    providerRoundId: PropTypes.string.isRequired,
    providerMatchId: PropTypes.string.isRequired,
    compSeasonId: PropTypes.string.isRequired
};
